import { useEffect, useState } from 'react';
import axios from 'axios';
import { extInSite } from './IvrShared';
import { useCustomer } from './customerCtx';
import api from '../services/ApiService';
import { ZoomTable, ZoomBtn, ZoomModal, ZoomField } from './zoom/ZoomTable';
import LocationSelect from './LocationSelect';

export default function DidsPage({ showTenantSelector, zoom, site }: { showTenantSelector?: boolean; zoom?: boolean; site?: any }) {
  const [dids, setDids]           = useState<any[]>([]);
  const [extensions, setExtensions] = useState<any[]>([]);
  const [queues, setQueues]       = useState<any[]>([]);
  const [ivrs, setIvrs] = useState<any[]>([]);
  const [tcs, setTcs] = useState<any[]>([]);
  const [prompts, setPrompts] = useState<any[]>([]);
  const dcust = useCustomer();
  useEffect(() => {
    axios.get('/api/asterisk/ivr').then((r) => setIvrs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get('/api/asterisk/time-conditions').then((r) => setTcs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get('/api/ivr-prompts').then((r) => setPrompts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  const [bots, setBots]           = useState<any[]>([]);
  const [tenants, setTenants]     = useState<any[]>([]);

  const [didNumber, setDidNumber]           = useState('');
  const [providerName, setProviderName]     = useState('');
  const [trunkName, setTrunkName]           = useState('');
  const [routingType, setRoutingType]       = useState('extension');
  const [destinationValue, setDestinationValue] = useState('');
  const [tenantId, setTenantId]             = useState('');
  const [filterTenant, setFilterTenant]     = useState('');
  const [showAddDid, setShowAddDid]         = useState(false);
  const [didSearch, setDidSearch]           = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  useEffect(() => { try {
    const t = localStorage.getItem('token');
    if (t) { const p = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      setIsSuperAdmin(p.role === 'super_admin' || p.is_admin === true || p.is_admin === 1); }
  } catch (_) { /* no token */ } }, []);

  const loadData = async () => {
    const calls: Promise<any>[] = [api.getDids(), api.getExtensions(), api.getQueues(),
      axios.get('/api/voice-bots').then(r => r.data)];
    if (showTenantSelector) calls.push((api as any).getAsteriskTenants());
    const [didRes, extRes, qRes, botRes, tenantRes] = await Promise.allSettled(calls);
    setDids(didRes.status === 'fulfilled' ? (didRes.value || []) : []);
    setExtensions(extRes.status === 'fulfilled' ? (extRes.value || []) : []);
    setQueues(qRes.status === 'fulfilled' ? (qRes.value || []) : []);
    const botList: any = botRes && botRes.status === 'fulfilled' ? botRes.value : [];
    setBots(Array.isArray(botList) ? botList : (botList?.bots || []));
    if (showTenantSelector && tenantRes?.status === 'fulfilled' && tenantRes.value) {
      setTenants(tenantRes.value);
      if (tenantRes.value.length > 0 && !tenantId) setTenantId(String(tenantRes.value[0].id));
    }
  };

  useEffect(() => { loadData(); }, []); // eslint-disable-line

  const createDid = async () => {
    if (!didNumber.trim()) { alert('DID number required'); return; }
    await api.createDid({
      did_number: didNumber.trim(),
      provider_name: providerName.trim(),
      trunk_name: trunkName.trim(),
      routing_type: routingType,
      destination_value: destinationValue,
      tenant_id: tenantId ? Number(tenantId) : undefined,
      is_active: true
    });
    setDidNumber(''); setProviderName(''); setTrunkName('');
    setRoutingType('extension'); setDestinationValue('');
    await loadData();
  };

  const updateDid = async (d: any, changes: any) => {
    const updated = { ...d, ...changes };
    try {
    await api.updateDid(d.id, {
      did_number: updated.did_number,
      provider_name: updated.provider_name,
      trunk_name: updated.trunk_name,
      routing_type: updated.routing_type,
      destination_value: updated.destination_value,
      is_active: !!updated.is_active
    });
    } catch (e: any) {
      alert('Could not save ' + (d.did_number || 'this number') + ': ' + (e?.response?.data?.error || e?.message || 'unknown error'));
    }
    await loadData();
  };

  const deleteDid = async (d: any) => {
    if (!window.confirm(`Delete DID ${d.did_number}?`)) return;
    await api.deleteDid(d.id);
    await loadData();
  };

  const tenantName = (id: any) =>
    tenants.find(t => t.id === Number(id))?.tenant_name || `Tenant ${id}`;

  const destinationOptions = (type: string, forTid?: any, keep?: any) => {
    if (type === 'extension') {
      const opts = extensions
        .filter(e => !showTenantSelector || !forTid || String(e.tenant_id) === String(forTid))
        .filter((e: any) => extInSite(e.extension_number, site))
        .filter((e: any) => dcust.match('extension', e.id))
        .map((e: any) => ({ label: `${e.extension_number} – ${e.display_name || ''}`, value: e.extension_number }));
      if (keep && !opts.some((o: any) => String(o.value) === String(keep))) opts.unshift({ label: String(keep), value: keep });
      return opts;
    }
    if (type === 'announcement') {
      const opts = prompts.filter((p: any) => !showTenantSelector || !forTid || String(p.tenant_id) === String(forTid))
        .map((p: any) => ({ label: 'Greeting: ' + p.name, value: p.sound }));
      if (keep && !opts.some((o: any) => String(o.value) === String(keep)))
        opts.unshift({ label: 'Recording: ' + String(keep).split('/').pop(), value: keep });
      return opts;
    }
    if (type === 'queue') {
      return queues
        .filter(q => !showTenantSelector || !forTid || String(q.tenant_id) === String(forTid))
        .map((q: any) => ({ label: `${q.queue_name} – ${q.display_name || ''}`, value: q.queue_name }));
    }
    if (type === 'ivr') {
      return ivrs.filter((m: any) => !showTenantSelector || !forTid || String(m.tenant_id) === String(forTid))
        .map((m: any) => ({ label: m.display_name || m.menu_name, value: m.menu_name }));
    }
    if (type === 'time_condition') {
      return tcs.filter((m: any) => !showTenantSelector || !forTid || String(m.tenant_id) === String(forTid))
        .map((m: any) => ({ label: m.display_name || m.condition_name, value: m.condition_name }));
    }
    if (type === 'voicebot') {
      // Receptionists (voice bots). The number is reserved for the one selected here.
      return bots
        .filter((b: any) => !showTenantSelector || !forTid || String(b.tenant_id) === String(forTid))
        .map((b: any) => ({ label: b.bot_name || b.name || String(b.id), value: String(b.id) }));
    }
    return [];
  };

  const visibleDids = (filterTenant
    ? dids.filter(d => String(d.tenant_id) === filterTenant)
    : dids)
    .filter((d: any) => !site || (site.gateways || []).includes(d.trunk_name))
    .filter((d: any) => dcust.match('did', d.id));

  // ── Zoom-console variant (TEC tenant) — reuses all the same state/handlers ──
  if (zoom) {
    const doCreateDid = async () => { await createDid(); setShowAddDid(false); };
    const zoomRows = visibleDids.filter((d: any) =>
      !didSearch.trim() || `${d.did_number} ${d.provider_name || ''} ${d.destination_value || ''}`.toLowerCase().includes(didSearch.toLowerCase()));

    const columns: any[] = [
      { key: 'did', label: 'Number', render: (d: any) => <div><div className="z-tbl-primary-cell">{d.did_number}</div>{dcust.enabled && dcust.tagOf('did', d.id) ? <div className="z-tbl-sub">{dcust.nameOf(dcust.tagOf('did', d.id))}</div> : null}</div> },
      { key: 'provider', label: 'Provider', render: (d: any) => (
        <input className="z-inline-input" style={{ width: 120 }} value={d.provider_name || ''} onChange={e => updateDid(d, { provider_name: e.target.value })} />
      ) },
      { key: 'trunk', label: 'Trunk', render: (d: any) => (
        <input className="z-inline-input" style={{ width: 110 }} value={d.trunk_name || ''} onChange={e => updateDid(d, { trunk_name: e.target.value })} />
      ) },
      { key: 'routing', label: 'Routing', render: (d: any) => (
        <select className="z-inline-select" value={d.routing_type || 'extension'} onChange={e => updateDid(d, { routing_type: e.target.value, destination_value: '' })}>
          <option value="extension">Extension</option>
          <option value="queue">Queue</option>
          <option value="external_forward">External Forward</option>
          <option value="ivr">IVR menu</option>
          <option value="time_condition">Business hours</option>
          <option value="announcement">Play greeting, then hang up</option>
          {d.routing_type === 'voicebot' && <option value="voicebot">Auto Receptionist</option>}
          {(isSuperAdmin || d.routing_type === 'meetme') && <option value="meetme">MeetMe Conference</option>} {/* didfix: every routing type */}
        </select>
      ) },
      { key: 'dest', label: 'Destination', render: (d: any) => (
        d.routing_type === 'external_forward'
          ? <input className="z-inline-input" style={{ width: 140 }} value={d.destination_value || ''} onChange={e => updateDid(d, { destination_value: e.target.value })} />
          : <select className="z-inline-select" value={d.destination_value || ''} onChange={e => updateDid(d, { destination_value: e.target.value })}>
              <option value="">Select destination</option>
              {destinationOptions(d.routing_type || 'extension', d.tenant_id, d.destination_value).map((o: any) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
      ) },
      { key: 'active', label: 'Status', render: (d: any) => (
        <button className={`z-badge ${d.is_active ? 'z-badge-green' : 'z-badge-red'}`} style={{ border: 'none', cursor: 'pointer' }}
          onClick={() => updateDid(d, { is_active: d.is_active ? 0 : 1 })}>{d.is_active ? 'Active' : 'Disabled'}</button>
      ) },
    ];
    columns.push({ key: 'location', label: 'Location', render: (d: any) => (
      <LocationSelect byId value={d.location_id} style={{ padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, maxWidth: 190 }}
        onChange={(v: any) => { axios.put('/api/locations/assign', { type: 'did', id: d.id, location_id: v }).then(() => { d.location_id = v; }).catch((e: any) => alert(e?.response?.data?.error || 'Could not set the location')); }} />) });
    if (showTenantSelector) columns.splice(1, 0, { key: 'tenant', label: 'Tenant', render: (d: any) => tenantName(d.tenant_id) });

    return (
      <>
        {showAddDid && (
          <ZoomModal title="Add Number (DID)" onClose={() => setShowAddDid(false)}
            footer={<><ZoomBtn onClick={() => setShowAddDid(false)}>Cancel</ZoomBtn><ZoomBtn variant="primary" onClick={doCreateDid}>Add</ZoomBtn></>}>
            {showTenantSelector && tenants.length > 0 && (
              <ZoomField label="Tenant">
                <select value={tenantId} onChange={e => setTenantId(e.target.value)}>
                  {tenants.map((t: any) => <option key={t.id} value={t.id}>{t.tenant_name}</option>)}
                </select>
              </ZoomField>
            )}
            <ZoomField label="DID Number"><input value={didNumber} onChange={e => setDidNumber(e.target.value)} placeholder="+971501001001" /></ZoomField>
            <div className="z-field-grid">
              <ZoomField label="Provider"><input value={providerName} onChange={e => setProviderName(e.target.value)} /></ZoomField>
              <ZoomField label="Trunk"><input value={trunkName} onChange={e => setTrunkName(e.target.value)} /></ZoomField>
            </div>
            <ZoomField label="Routing Type">
              <select value={routingType} onChange={e => { setRoutingType(e.target.value); setDestinationValue(''); }}>
                <option value="extension">Extension</option>
                <option value="queue">Queue</option>
                <option value="external_forward">External Forward</option>
          <option value="ivr">IVR menu</option>
          <option value="time_condition">Business hours</option>
          <option value="announcement">Play greeting, then hang up</option>
          {routingType === 'voicebot' && <option value="voicebot">Auto Receptionist</option>}
          {(isSuperAdmin || routingType === 'meetme') && <option value="meetme">MeetMe Conference</option>} {/* didfix: every routing type */}
              </select>
            </ZoomField>
            <ZoomField label="Destination">
              {routingType === 'external_forward'
                ? <input value={destinationValue} onChange={e => setDestinationValue(e.target.value)} placeholder="External number" />
                : <select value={destinationValue} onChange={e => setDestinationValue(e.target.value)}>
                    <option value="">Select destination</option>
                    {destinationOptions(routingType, tenantId).map((o: any) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>}
            </ZoomField>
          </ZoomModal>
        )}

        <ZoomTable
          title="Phone Numbers"
          subtitle="Manage inbound DID routing to extensions, queues, or external forwarding."
          search={didSearch}
          onSearch={setDidSearch}
          searchPlaceholder="Search numbers"
          actions={<>
            <ZoomBtn onClick={() => alert('Bulk import — coming soon')}>Import</ZoomBtn>
            <ZoomBtn variant="primary" onClick={() => setShowAddDid(true)}>+ Add Number</ZoomBtn>
          </>}
          columns={columns}
          rows={zoomRows}
          rowKey={(d: any) => d.id}
          rowActions={(d: any) => <ZoomBtn variant="danger" onClick={() => deleteDid(d)}>Delete</ZoomBtn>}
          emptyText="No numbers yet"
        />
      </>
    );
  }

  return (
    <>
      <div className="admin-page-title">DID Numbers</div>
      <div className="admin-page-subtitle">
        Manage inbound DID routing to extensions, queues, or external forwarding.
      </div>

      {/* Tenant filter bar */}
      {showTenantSelector && tenants.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>Tenant filter:</span>
          <button onClick={() => setFilterTenant('')} style={{
            padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', border: 'none',
            background: !filterTenant ? '#8b5cf6' : 'rgba(100,116,139,0.2)',
            color: !filterTenant ? '#fff' : '#94a3b8', fontWeight: !filterTenant ? 700 : 400,
          }}>All</button>
          {tenants.map((t: any) => (
            <button key={t.id} onClick={() => setFilterTenant(String(t.id))} style={{
              padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', border: 'none',
              background: filterTenant === String(t.id) ? '#8b5cf6' : 'rgba(100,116,139,0.2)',
              color: filterTenant === String(t.id) ? '#fff' : '#94a3b8',
              fontWeight: filterTenant === String(t.id) ? 700 : 400,
            }}>{t.tenant_name}</button>
          ))}
          <span style={{ fontSize: 11, color: '#475569', marginLeft: 6 }}>
            {visibleDids.length} DID{visibleDids.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      <div className="content-card">
        <h3>Create DID</h3>
        <div className="admin-form-row">
          {showTenantSelector && tenants.length > 0 && (
            <select className="admin-select" value={tenantId} onChange={e => setTenantId(e.target.value)} title="Assign to tenant">
              {tenants.map((t: any) => <option key={t.id} value={t.id}>{t.tenant_name}</option>)}
            </select>
          )}
          <input className="admin-input" value={didNumber} onChange={e => setDidNumber(e.target.value)} placeholder="+971501001001" />
          <input className="admin-input" value={providerName} onChange={e => setProviderName(e.target.value)} placeholder="Provider" />
          <input className="admin-input" value={trunkName} onChange={e => setTrunkName(e.target.value)} placeholder="Trunk" />
          <select className="admin-select" value={routingType} onChange={e => { setRoutingType(e.target.value); setDestinationValue(''); }}>
            <option value="extension">Extension</option>
            <option value="queue">Queue</option>
            <option value="external_forward">External Forward</option>
          <option value="ivr">IVR menu</option>
          <option value="time_condition">Business hours</option>
          <option value="announcement">Play greeting, then hang up</option>
          {routingType === 'voicebot' && <option value="voicebot">Auto Receptionist</option>}
          {(isSuperAdmin || routingType === 'meetme') && <option value="meetme">MeetMe Conference</option>} {/* didfix: every routing type */}
          </select>
          {routingType === 'external_forward' ? (
            <input className="admin-input" value={destinationValue} onChange={e => setDestinationValue(e.target.value)} placeholder="External number" />
          ) : (
            <select className="admin-select" value={destinationValue} onChange={e => setDestinationValue(e.target.value)}>
              <option value="">Select destination</option>
              {destinationOptions(routingType, tenantId).map((o: any) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}
          <button className="primary-btn" onClick={createDid}>Create DID</button>
        </div>
      </div>

      <div className="content-card">
        <h3>DID Routing</h3>
        <table className="admin-table">
          <thead>
            <tr>
              <th>DID</th>
              {showTenantSelector && <th>Tenant</th>}
              <th>Provider</th>
              <th>Trunk</th>
              <th>Routing Type</th>
              <th>Destination</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleDids.map((d: any) => (
              <tr key={d.id}>
                <td><strong>{d.did_number}</strong></td>
                {showTenantSelector && (
                  <td>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'rgba(139,92,246,0.15)', color: '#c4b5fd' }}>
                      {tenantName(d.tenant_id)}
                    </span>
                  </td>
                )}
                <td>
                  <input className="admin-input" value={d.provider_name || ''}
                    onChange={e => updateDid(d, { provider_name: e.target.value })} />
                </td>
                <td>
                  <input className="admin-input" value={d.trunk_name || ''}
                    onChange={e => updateDid(d, { trunk_name: e.target.value })} />
                </td>
                <td>
                  <select className="admin-select" value={d.routing_type || 'extension'}
                    onChange={e => updateDid(d, { routing_type: e.target.value, destination_value: '' })}>
                    <option value="extension">Extension</option>
                    <option value="queue">Queue</option>
                    <option value="external_forward">External Forward</option>
          <option value="ivr">IVR menu</option>
          <option value="time_condition">Business hours</option>
          <option value="announcement">Play greeting, then hang up</option>
          {d.routing_type === 'voicebot' && <option value="voicebot">Auto Receptionist</option>}
          {(isSuperAdmin || d.routing_type === 'meetme') && <option value="meetme">MeetMe Conference</option>} {/* didfix: every routing type */}
                  </select>
                </td>
                <td>
                  {d.routing_type === 'external_forward' ? (
                    <input className="admin-input" value={d.destination_value || ''}
                      onChange={e => updateDid(d, { destination_value: e.target.value })} />
                  ) : (
                    <select className="admin-select" value={d.destination_value || ''}
                      onChange={e => updateDid(d, { destination_value: e.target.value })}>
                      <option value="">Select destination</option>
                      {destinationOptions(d.routing_type || 'extension', d.tenant_id, d.destination_value).map((o: any) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  )}
                </td>
                <td><Toggle enabled={!!d.is_active} onClick={() => updateDid(d, { is_active: d.is_active ? 0 : 1 })} /></td>
                <td><button className="danger-btn" onClick={() => deleteDid(d)}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Toggle({ enabled, onClick }: any) {
  return (
    <button className={`admin-pill ${enabled ? 'green' : 'red'}`} onClick={onClick} type="button">
      {enabled ? 'ON' : 'OFF'}
    </button>
  );
}
